import React, { useState, useEffect, useRef } from "react";

const AI_API_URL = process.env.REACT_APP_AI_API_URL;

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface UserLite {
  id: string;
  username: string;
  email: string;
}

interface UserProfile {
  generated_at: string;
  text_iris_total: number;
  product_searches: { ts: string; lens_url: string; channel?: string }[];
  quizzes: {
    summary: { answered: number; correct: number; incorrect: number };
    recent: { ts: string; quiz_id: string; correct: boolean; channel?: string }[];
  };
  chat_messages: { created_at: string; message: string; channel?: string }[];
  profile_summary: string;
  for_advertisers: string;
}

const EMPTY_PROFILE: UserProfile = {
  generated_at: "",
  text_iris_total: 0,
  product_searches: [],
  quizzes: { summary: { answered: 0, correct: 0, incorrect: 0 }, recent: [] },
  chat_messages: [],
  profile_summary: "",
  for_advertisers: "",
};

export const UserSearchModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<UserLite[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedUser, setSelectedUser] = useState<UserLite | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [profile, setProfile] = useState<UserProfile>(EMPTY_PROFILE);
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setQuery("");
      setResults([]);
      setSelectedUser(null);
      setError("");
    }
  }, [isOpen]);

  const runSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setError("");
    setSelectedUser(null);
    
    try {
      const r = await fetch(
        `${AI_API_URL}/search-users?q=${encodeURIComponent(query.trim())}`
      );
      if (!r.ok) throw new Error("Search failed");
      const json = await r.json();
      setResults(json.results || []);
    } catch (err: any) {
      setError(err.message || "Search failed");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = async (user: UserLite) => {
    setSelectedUser(user);
    setProfile(EMPTY_PROFILE);
    setProfileLoading(true);
    try {
      const r = await fetch(
        `${AI_API_URL}/user-profile/${user.id}?iris_limit=50&lens_limit=30&quiz_limit=50&chat_limit=40`
      );
      if (!r.ok) throw new Error("Profile fetch failed");
      const json = await r.json();
      setProfile(json);
    } catch (err) {
      console.error(err);
      setError("Could not load user profile");
    } finally {
      setProfileLoading(false);
    }
  };


  const handleRefresh = async () => {
    if (!selectedUser) return;
    
    setProfileLoading(true);
    setError("");
    
    try {
        const r = await fetch(
        `${AI_API_URL}/user-profile/${selectedUser.id}?iris_limit=50&lens_limit=30&quiz_limit=50&chat_limit=40&force_refresh=true`
        );
        if (!r.ok) throw new Error("Profile refresh failed");
        const json = await r.json();
        setProfile(json);
    } catch (err) {
        console.error(err);
        setError("Could not refresh user profile");
    } finally {
        setProfileLoading(false);
    }
    };

  const handleBack = () => {
    setSelectedUser(null);
  };

  if (!isOpen) return null;

  // Professional Metric Card Component
  const MetricCard = ({ 
    title, 
    value, 
    subtitle, 
    icon, 
    gradient,
    trend,
    delay = 0
  }: {
    title: string;
    value: string | number;
    subtitle?: string;
    icon: React.ReactNode;
    gradient: string;
    trend?: { value: number; direction: 'up' | 'down' | 'neutral' };
    delay?: number;
  }) => (
    <div 
      style={{
        background: `linear-gradient(135deg, ${gradient})`,
        border: "1px solid rgba(255, 255, 255, 0.08)",
        borderRadius: "20px",
        padding: "28px",
        position: "relative",
        overflow: "hidden",
        backdropFilter: "blur(20px)",
        boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        animation: `slideUpStagger 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${delay}ms both`,
        cursor: "pointer"
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-4px) scale(1.02)";
        e.currentTarget.style.boxShadow = "0 20px 60px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.15)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0) scale(1)";
        e.currentTarget.style.boxShadow = "0 10px 40px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)";
      }}
    >
      {/* Animated background pattern */}
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.05) 0%, transparent 50%)",
        pointerEvents: "none"
      }} />
      
      <div style={{ position: "relative", zIndex: 2 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "20px" }}>
          <div style={{
            width: "56px",
            height: "56px",
            borderRadius: "16px",
            background: "rgba(255, 255, 255, 0.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.08)"
          }}>
            {icon}
          </div>
          {trend && (
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "6px 12px",
              borderRadius: "24px",
              background: trend.direction === 'up' ? "rgba(34, 197, 94, 0.15)" : 
                          trend.direction === 'down' ? "rgba(239, 68, 68, 0.15)" : "rgba(156, 163, 175, 0.15)",
              border: trend.direction === 'up' ? "1px solid rgba(34, 197, 94, 0.2)" : 
                      trend.direction === 'down' ? "1px solid rgba(239, 68, 68, 0.2)" : "1px solid rgba(156, 163, 175, 0.2)",
              fontSize: "12px",
              fontWeight: "700",
              color: trend.direction === 'up' ? "#22c55e" : 
                     trend.direction === 'down' ? "#ef4444" : "#9ca3af"
            }}>
              {trend.direction === 'up' && "↗"}
              {trend.direction === 'down' && "↘"}
              {trend.direction === 'neutral' && "→"}
              {Math.abs(trend.value)}%
            </div>
          )}
        </div>
        
        <div style={{ 
          color: "#ffffff", 
          fontSize: "36px", 
          fontWeight: "800", 
          lineHeight: "1", 
          marginBottom: "12px",
          fontFamily: "system-ui, -apple-system, sans-serif"
        }}>
          {value}
        </div>
        
        <div style={{ 
          color: "rgba(255, 255, 255, 0.95)", 
          fontSize: "16px", 
          fontWeight: "600", 
          marginBottom: "6px",
          letterSpacing: "-0.01em"
        }}>
          {title}
        </div>
        
        {subtitle && (
          <div style={{ 
            color: "rgba(255, 255, 255, 0.7)", 
            fontSize: "13px",
            fontWeight: "500"
          }}>
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );

  // Professional Activity Section
  const ActivitySection = <T,>({
    title,
    data,
    render,
    icon,
    emptyMessage = "No recent activity",
    gradient = "rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.01)"
  }: {
    title: string;
    data: T[];
    render: (item: T, index: number) => React.ReactNode;
    icon: React.ReactNode;
    emptyMessage?: string;
    gradient?: string;
  }) => (
    <div style={{
      background: `linear-gradient(135deg, ${gradient})`,
      border: "1px solid rgba(255, 255, 255, 0.06)",
      borderRadius: "24px",
      overflow: "hidden",
      backdropFilter: "blur(20px)",
      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)"
    }}>
      {/* Section Header */}
      <div className="activity-section-header" style={{
        padding: "32px 32px 24px",
        borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
        background: "rgba(255, 255, 255, 0.02)"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div style={{
            width: "48px",
            height: "48px",
            borderRadius: "14px",
            background: "linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(139, 92, 246, 0.2))",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px solid rgba(139, 92, 246, 0.15)"
          }}>
            {icon}
          </div>
          <div>
            <h3 style={{
              color: "#ffffff",
              fontSize: "20px",
              fontWeight: "700",
              margin: "0 0 4px 0",
              letterSpacing: "-0.02em"
            }}>
              {title}
            </h3>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}>
              <div style={{
                color: "rgba(255, 255, 255, 0.6)",
                fontSize: "14px",
                fontWeight: "500"
              }}>
                {data.length} items
              </div>
              <div style={{
                width: "4px",
                height: "4px",
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.3)"
              }} />
              <div style={{
                color: "rgba(255, 255, 255, 0.5)",
                fontSize: "13px",
                fontWeight: "500"
              }}>
                Last updated recently
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section Content */}
      <div className="activity-section-content" style={{ padding: "28px 32px 32px" }}>
        {data.length === 0 ? (
          <div style={{
            textAlign: "center",
            padding: "48px 24px",
            color: "rgba(255, 255, 255, 0.4)"
          }}>
            <div style={{ 
              fontSize: "56px", 
              marginBottom: "20px", 
              opacity: 0.3,
              filter: "grayscale(1)"
            }}>
              📭
            </div>
            <div style={{ 
              fontSize: "18px", 
              fontWeight: "600", 
              marginBottom: "8px",
              color: "rgba(255, 255, 255, 0.6)"
            }}>
              {emptyMessage}
            </div>
            <div style={{ 
              fontSize: "14px", 
              color: "rgba(255, 255, 255, 0.4)"
            }}>
              Data will appear here when available
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {data.slice(-6).reverse().map((item, idx) => (
              <div 
                key={idx} 
                style={{
                  background: "rgba(255, 255, 255, 0.02)",
                  border: "1px solid rgba(255, 255, 255, 0.04)",
                  borderRadius: "16px",
                  padding: "20px 24px",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.04)";
                  e.currentTarget.style.transform = "translateX(4px)";
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.02)";
                  e.currentTarget.style.transform = "translateX(0)";
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.04)";
                }}
              >
                {/* Subtle gradient overlay */}
                <div style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "3px",
                  height: "100%",
                  background: "linear-gradient(135deg, #60a5fa, #3b82f6)",
                  opacity: 0.6
                }} />
                
                <div style={{ marginLeft: "12px" }}>
                  {render(item, idx)}
                </div>
              </div>
            ))}
            
            {data.length > 6 && (
              <div style={{
                textAlign: "center",
                padding: "16px",
                color: "rgba(255, 255, 255, 0.5)",
                fontSize: "14px",
                fontWeight: "500"
              }}>
                + {data.length - 6} more items
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }
          
          @keyframes slideUp {
            0% { 
              opacity: 0; 
              transform: translate(-50%, -50%) scale(0.95) translateY(20px); 
            }
            100% { 
              opacity: 1; 
              transform: translate(-50%, -50%) scale(1) translateY(0); 
            }
          }

          @keyframes slideUpStagger {
            0% { 
              opacity: 0; 
              transform: translateY(30px) scale(0.95); 
            }
            100% { 
              opacity: 1; 
              transform: translateY(0) scale(1); 
            }
          }

          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }

          .loading-shimmer {
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
            animation: shimmer 1.5s infinite;
          }
          
          .user-item {
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          }
          
          .user-item:hover {
            background: rgba(255, 255, 255, 0.1) !important;
            transform: translateY(-2px);
            box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3);
          }
          
          .search-input:focus {
            border-color: rgba(96, 165, 250, 0.5) !important;
            box-shadow: 0 0 0 4px rgba(96, 165, 250, 0.1) !important;
          }
          
          .search-button:hover:not(:disabled) {
            background: rgba(59, 130, 246, 1) !important;
            transform: translateY(-1px);
            box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
          }
          
          .back-button:hover {
            background: rgba(255, 255, 255, 0.08) !important;
            transform: translateX(-4px);
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          .profile-scrollbar::-webkit-scrollbar {
            width: 8px;
            }

            .profile-scrollbar::-webkit-scrollbar-track {
            background: rgba(255, 255, 255, 0.05);
            border-radius: 10px;
            }

            .profile-scrollbar::-webkit-scrollbar-thumb {
            background: linear-gradient(135deg, rgba(139, 92, 246, 0.6), rgba(99, 102, 241, 0.6));
            border-radius: 10px;
            border: 1px solid rgba(255, 255, 255, 0.1);
            }

            .profile-scrollbar::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(135deg, rgba(139, 92, 246, 0.8), rgba(99, 102, 241, 0.8));
            }

            /* Firefox */
            .profile-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: rgba(139, 92, 246, 0.6) rgba(255, 255, 255, 0.05);
            }

            @media (max-width: 768px) {
            .modal-dialog {
                width: 98% !important;
                maxWidth: none !important;
                margin: 1% !important;
                borderRadius: 16px !important;
            }
            
            .modal-header {
                padding: 24px 20px 20px !important;
            }
            
            .modal-content {
                padding: 24px 20px 32px !important;
            }
            
            .metrics-grid {
                gridTemplateColumns: 1fr !important;
                gap: 16px !important;
            }
            
            .insights-grid {
                gridTemplateColumns: 1fr !important;
                gap: 16px !important;
            }
            
            .button-container {
                flexDirection: column !important;
                gap: 12px !important;
                alignItems: stretch !important;
            }
            
            .user-header-card {
                padding: 20px !important;
            }
            
            .user-header-content {
                flexDirection: column !important;
                textAlign: center !important;
                gap: 16px !important;
            }
            
            .activity-section-header {
                padding: 20px 20px 16px !important;
            }
            
            .activity-section-content {
                padding: 16px 20px 20px !important;
            }
            }

            @media (max-width: 480px) {
            .modal-dialog {
                width: 100% !important;
                height: 100% !important;
                maxHeight: 100vh !important;
                borderRadius: 0 !important;
                transform: none !important;
                top: 0 !important;
                left: 0 !important;
            }
            
            .modal-header h2 {
                fontSize: 24px !important;
            }
            
            .modal-header p {
                fontSize: 14px !important;
            }
            }
        `}
      </style>
      
      <div
        role="presentation"
        onKeyDown={(e) => e.key === "Escape" && onClose()}
        tabIndex={-1}
        style={{
          position: "fixed",
          inset: 0,
          backdropFilter: "blur(16px)",
          background: "rgba(0, 0, 0, 0.92)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
          animation: "fadeIn 0.4s ease-out",
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          className={`${selectedUser ? "profile-scrollbar" : ""} modal-dialog`}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "95%",
            maxWidth: selectedUser ? "1400px" : "900px",
            background: "rgba(15, 23, 42, 0.95)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            borderRadius: "28px",
            backdropFilter: "blur(40px)",
            boxShadow: "0 32px 64px -12px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.05)",
            maxHeight: selectedUser ? "85vh" : "92vh",
            overflowY: "auto",
            animation: "slideUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {/* Enhanced Header */}
          <div className="modal-header" style={{ 
            padding: "40px 48px 32px",
            borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
            background: "linear-gradient(135deg, rgba(255, 255, 255, 0.02) 0%, rgba(255, 255, 255, 0.01) 100%)"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h2 style={{ 
                  margin: "0 0 12px 0", 
                  color: "#ffffff", 
                  fontSize: "32px", 
                  fontWeight: "800",
                  letterSpacing: "-0.03em",
                  background: "linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent"
                }}>
                  User Personalization Hub
                </h2>
                <p style={{ 
                  margin: 0, 
                  color: "rgba(255, 255, 255, 0.7)", 
                  fontSize: "17px",
                  fontWeight: "500",
                  letterSpacing: "-0.01em"
                }}>
                  Advanced insights into user engagement and behavioral patterns
                </p>
              </div>
              <button 
                type="button"
                onClick={onClose}
                style={{ 
                  background: "rgba(255, 255, 255, 0.06)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "16px",
                  color: "#ffffff",
                  padding: "16px 20px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "600",
                  transition: "all 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px"
                }}
                aria-label="Close modal"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                Close
              </button>
            </div>
          </div>

          <div className="modal-content" style={{ padding: "40px 48px 48px" }}>
            {/* Professional User Profile Dashboard */}
            {selectedUser ? (
              <div style={{ animation: "slideUpStagger 0.4s ease-out" }}>
                {/* Enhanced Back Button */}

                <div className="button-container" style={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "center", 
                marginBottom: "32px" 
                }}>
                <button
                    type="button"
                    onClick={handleBack}
                    className="back-button"
                    style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "16px 24px",
                    background: "rgba(255, 255, 255, 0.04)",
                    border: "1px solid rgba(255, 255, 255, 0.08)",
                    borderRadius: "16px",
                    color: "rgba(255, 255, 255, 0.9)",
                    fontSize: "15px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.3s ease"
                    }}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 12H5" />
                    <polyline points="12 19 5 12 12 5" />
                    </svg>
                    Back to Search
                </button>

                <button
                    type="button"
                    onClick={handleRefresh}
                    disabled={profileLoading}
                    style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "16px 24px",
                    background: "rgba(255, 255, 255, 0.04)",
                    border: "1px solid rgba(255, 255, 255, 0.08)",
                    borderRadius: "16px",
                    color: "rgba(255, 255, 255, 0.9)",
                    fontSize: "15px",
                    fontWeight: "600",
                    cursor: profileLoading ? "not-allowed" : "pointer",
                    transition: "all 0.3s ease",
                    opacity: profileLoading ? 0.6 : 1
                    }}
                    className="back-button" // Use the same hover class
                >
                    {profileLoading ? (
                    <>
                        <div style={{
                        width: "16px",
                        height: "16px",
                        border: "2px solid rgba(255,255,255,0.3)",
                        borderTop: "2px solid rgba(255,255,255,0.9)",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite"
                        }} />
                        Refreshing...
                    </>
                    ) : (
                    <>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="23,4 23,10 17,10"/>
                        <polyline points="1,20 1,14 7,14"/>
                        <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/>
                        </svg>
                        Refresh
                    </>
                    )}
                </button>
                </div>

                {profileLoading ? (
                  <div style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center", 
                    minHeight: "500px",
                    flexDirection: "column",
                    gap: "24px"
                  }}>
                    <div style={{
                      width: "72px",
                      height: "72px",
                      border: "4px solid rgba(96, 165, 250, 0.2)",
                      borderTop: "4px solid #60a5fa",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite"
                    }} />
                    <div style={{ 
                      color: "rgba(255, 255, 255, 0.8)", 
                      fontSize: "20px", 
                      fontWeight: "600",
                      textAlign: "center"
                    }}>
                      <div>Loading comprehensive analytics...</div>
                      <div style={{ 
                        fontSize: "14px", 
                        color: "rgba(255, 255, 255, 0.5)", 
                        marginTop: "8px",
                        fontWeight: "500"
                      }}>
                        Analyzing user engagement patterns
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Professional User Header Card */}
                    <div className="user-header-card" style={{
                      background: "linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)",
                      border: "1px solid rgba(139, 92, 246, 0.15)",
                      borderRadius: "24px",
                      padding: "32px",
                      marginBottom: "32px",
                      position: "relative",
                      overflow: "hidden"
                    }}>
                      {/* Decorative background pattern */}
                      <div style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        width: "200px",
                        height: "200px",
                        background: "radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)",
                        pointerEvents: "none"
                      }} />
                      
                      <div className="user-header-content" style={{ display: "flex", alignItems: "center", gap: "24px", position: "relative", zIndex: 1 }}>
                        <div style={{
                          width: "80px",
                          height: "80px",
                          borderRadius: "20px",
                          background: "linear-gradient(135deg, #60a5fa, #3b82f6)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          boxShadow: "0 8px 32px rgba(96, 165, 250, 0.3)"
                        }}>
                          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
                            <path d="M20 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M4 21v-2a4 4 0 0 1 3-3.87" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                        </div>
                        
                        <div style={{ flex: 1 }}>
                          <h3 style={{ 
                            margin: "0 0 8px 0", 
                            color: "#ffffff", 
                            fontSize: "28px", 
                            fontWeight: "700",
                            letterSpacing: "-0.02em"
                          }}>
                            {selectedUser.username}
                          </h3>
                          <p style={{ 
                            margin: "0 0 12px 0", 
                            color: "rgba(255, 255, 255, 0.8)", 
                            fontSize: "16px",
                            fontWeight: "500"
                          }}>
                            {selectedUser.email}
                          </p>
                          <div style={{
                            display: "inline-block",
                            padding: "8px 16px",
                            background: "rgba(96, 165, 250, 0.15)",
                            border: "1px solid rgba(96, 165, 250, 0.25)",
                            borderRadius: "12px",
                            color: "#60a5fa",
                            fontSize: "13px",
                            fontWeight: "600",
                            fontFamily: "ui-monospace, monospace"
                          }}>
                            User ID: {selectedUser.id}
                          </div>
                        </div>

                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          color: "rgba(255, 255, 255, 0.6)",
                          fontSize: "14px",
                          fontWeight: "500"
                        }}>
                          <div style={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            background: "#22c55e",
                            boxShadow: "0 0 8px rgba(34, 197, 94, 0.4)"
                          }} />
                          Active Profile
                        </div>
                      </div>
                    </div>

                    {/* Professional Metrics Grid */}
                    <div className="metrics-grid" style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                      gap: "24px",
                      marginBottom: "40px"
                    }}>
                      {([
                        {
                          title: "Iris Q&A Messages",
                          value: profile.text_iris_total,
                          subtitle: "Total interactions",
                            icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
                            <rect x="4" y="4" width="16" height="16" rx="2"/>
                            <rect x="9" y="9" width="6" height="6"/>
                            <line x1="9" y1="1" x2="9" y2="4"/>
                            <line x1="15" y1="1" x2="15" y2="4"/>
                            <line x1="9" y1="20" x2="9" y2="23"/>
                            <line x1="15" y1="20" x2="15" y2="23"/>
                            <line x1="20" y1="9" x2="23" y2="9"/>
                            <line x1="20" y1="14" x2="23" y2="14"/>
                            <line x1="1" y1="9" x2="4" y2="9"/>
                            <line x1="1" y1="14" x2="4" y2="14"/>
                            </svg>,
                          gradient: "rgba(34, 197, 94, 0.15), rgba(34, 197, 94, 0.05)",
                          trend: { value: 12, direction: 'up' as const }
                        },
                        {
                          title: "Product Searches",
                          value: profile.product_searches.length,
                          subtitle: "Discovery activities",
                          icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
                            <circle cx="11" cy="11" r="8"/>
                            <path d="M21 21l-4.35-4.35"/>
                          </svg>,
                          gradient: "rgba(59, 130, 246, 0.15), rgba(59, 130, 246, 0.05)",
                          trend: { value: 8, direction: 'up' as const }
                        },
                        {
                          title: "Chat Messages",
                          value: profile.chat_messages.length >= 40 ? `${profile.chat_messages.length}+` : profile.chat_messages.length,
                          subtitle: "Communication volume",
                          icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                          </svg>,
                          gradient: "rgba(139, 92, 246, 0.15), rgba(139, 92, 246, 0.05)",
                          trend: { value: 5, direction: 'neutral' as const }
                        },
                        {
                          title: "Quiz Success Rate",
                          value: profile.quizzes.summary.correct + profile.quizzes.summary.incorrect > 0 ? 
                            `${Math.round((profile.quizzes.summary.correct / (profile.quizzes.summary.correct + profile.quizzes.summary.incorrect)) * 100)}%` : 
                            "0%",
                          subtitle: `${profile.quizzes.summary.correct}/${profile.quizzes.summary.correct + profile.quizzes.summary.incorrect} correct`,
                          icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
                            <polyline points="20,6 9,17 4,12"/>
                          </svg>,
                          gradient: "rgba(245, 158, 11, 0.15), rgba(245, 158, 11, 0.05)",
                          trend: { value: 15, direction: 'up' as const }
                        }
                      ]).map((metric, index) => (
                        <MetricCard 
                          key={metric.title}
                          {...metric}
                          delay={index * 100}
                        />
                      ))}
                    </div>

                    {/* AI-Generated Insights */}
                    {(profile.profile_summary || profile.for_advertisers) && (
                      <div className="insights-grid" style={{
                        display: "grid",
                        gridTemplateColumns: profile.profile_summary && profile.for_advertisers ? "1fr 1fr" : "1fr",
                        gap: "24px",
                        marginBottom: "40px"
                      }}>
                        {profile.profile_summary && (
                          <div style={{
                            background: "linear-gradient(135deg, rgba(34, 197, 94, 0.08), rgba(34, 197, 94, 0.03))",
                            border: "1px solid rgba(34, 197, 94, 0.15)",
                            borderRadius: "20px",
                            padding: "28px",
                            position: "relative",
                            overflow: "hidden"
                          }}>
                            <div style={{
                              position: "absolute",
                              top: "16px",
                              right: "16px",
                              width: "40px",
                              height: "40px",
                              borderRadius: "10px",
                              background: "rgba(34, 197, 94, 0.1)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}>
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
                                <path d="M9 11H5a2 2 0 0 0-2 2v3c0 1.1.9 2 2 2h4m6-6h4a2 2 0 0 1 2 2v3c0 1.1-.9 2-2 2h-4"/>
                                <path d="M5 19v-4a8 8 0 0 1 16-0v4"/>
                              </svg>
                            </div>
                            <h4 style={{ 
                              color: "#22c55e", 
                              fontSize: "18px", 
                              fontWeight: "700", 
                              margin: "0 0 16px",
                              letterSpacing: "-0.01em"
                            }}>
                              Personalization Profile
                            </h4>
                            <p style={{ 
                              color: "rgba(255, 255, 255, 0.9)", 
                              fontSize: "15px", 
                              margin: 0, 
                              lineHeight: "1.6",
                              fontWeight: "500"
                            }}>
                              {profile.profile_summary}
                            </p>
                          </div>
                        )}

                        {profile.for_advertisers && (
                          <div style={{
                            background: "linear-gradient(135deg, rgba(99, 102, 241, 0.08), rgba(99, 102, 241, 0.03))",
                            border: "1px solid rgba(99, 102, 241, 0.15)",
                            borderRadius: "20px",
                            padding: "28px",
                            position: "relative",
                            overflow: "hidden"
                          }}>
                            <div style={{
                              position: "absolute",
                              top: "16px",
                              right: "16px",
                              width: "40px",
                              height: "40px",
                              borderRadius: "10px",
                              background: "rgba(99, 102, 241, 0.1)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}>
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2">
                                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                                <line x1="8" y1="21" x2="16" y2="21"/>
                                <line x1="12" y1="17" x2="12" y2="21"/>
                              </svg>
                            </div>
                            <h4 style={{ 
                              color: "#6366f1", 
                              fontSize: "18px", 
                              fontWeight: "700", 
                              margin: "0 0 16px",
                              letterSpacing: "-0.01em"
                            }}>
                              For Advertisers
                            </h4>
                            <p style={{ 
                              color: "rgba(255, 255, 255, 0.9)", 
                              fontSize: "15px", 
                              margin: 0, 
                              lineHeight: "1.6",
                              fontWeight: "500"
                            }}>
                              {profile.for_advertisers}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Activity Sections */}
                    <div style={{
                      display: "grid",
                      gap: "32px"
                    }}>
                      <ActivitySection 
                        title="Product Search History" 
                        data={profile.product_searches} 
                        icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2">
                          <circle cx="11" cy="11" r="8"/>
                          <path d="M21 21l-4.35-4.35"/>
                        </svg>}
                        gradient="rgba(139, 92, 246, 0.04), rgba(139, 92, 246, 0.01)"
                        render={(item) => (
                          <div>
                            <a 
                              href={item.lens_url} 
                              target="_blank" 
                              rel="noreferrer" 
                              style={{ 
                                color: "#8b5cf6", 
                                textDecoration: "none", 
                                fontWeight: "600",
                                fontSize: "14px",
                                display: "block",
                                marginBottom: "6px"
                              }}
                            >
                              {item.lens_url.length > 80 ? `${item.lens_url.slice(0, 80)}...` : item.lens_url}
                            </a>
                            <div style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              fontSize: "12px",
                              color: "rgba(255, 255, 255, 0.5)"
                            }}>
                              <span>🕒 {new Date(item.ts).toLocaleDateString()}</span>
                              {item.channel && (
                                <>
                                  <span>•</span>
                                  <span style={{
                                    background: "rgba(139, 92, 246, 0.2)",
                                    padding: "2px 8px",
                                    borderRadius: "8px",
                                    color: "#a855f7",
                                    fontWeight: "600"
                                  }}>
                                    {item.channel}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                        emptyMessage="No product searches recorded"
                      />

                      <ActivitySection 
                        title="Quiz Performance" 
                        data={profile.quizzes.recent} 
                        icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                          <line x1="12" y1="17" x2="12.01" y2="17"/>
                        </svg>}
                        gradient="rgba(245, 158, 11, 0.04), rgba(245, 158, 11, 0.01)"
                        render={(item) => (
                          <div>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "6px" }}>
                              <code style={{
                                background: "rgba(245, 158, 11, 0.1)",
                                color: "#f59e0b",
                                padding: "4px 8px",
                                borderRadius: "6px",
                                fontSize: "12px",
                                fontWeight: "600"
                              }}>
                                {item.quiz_id}
                              </code>
                              <span style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "4px 12px",
                                borderRadius: "12px",
                                fontSize: "12px",
                                fontWeight: "700",
                                background: item.correct ? "rgba(34, 197, 94, 0.15)" : "rgba(239, 68, 68, 0.15)",
                                color: item.correct ? "#22c55e" : "#ef4444",
                                border: item.correct ? "1px solid rgba(34, 197, 94, 0.2)" : "1px solid rgba(239, 68, 68, 0.2)"
                              }}>
                                {item.correct ? "✅ Correct" : "❌ Incorrect"}
                              </span>
                            </div>
                            <div style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              fontSize: "12px",
                              color: "rgba(255, 255, 255, 0.5)"
                            }}>
                              <span>🕒 {new Date(item.ts).toLocaleDateString()}</span>
                              {item.channel && (
                                <>
                                  <span>•</span>
                                  <span style={{
                                    background: "rgba(245, 158, 11, 0.2)",
                                    padding: "2px 8px",
                                    borderRadius: "8px",
                                    color: "#f59e0b",
                                    fontWeight: "600"
                                  }}>
                                    {item.channel}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                        emptyMessage="No quiz attempts recorded"
                      />

                      <ActivitySection 
                        title="Recent Chat Messages" 
                        data={profile.chat_messages} 
                        icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#06b6d4" strokeWidth="2">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                          <line x1="10" y1="9" x2="16" y2="9"/>
                          <line x1="10" y1="13" x2="14" y2="13"/>
                        </svg>}
                        gradient="rgba(6, 182, 212, 0.04), rgba(6, 182, 212, 0.01)"
                        render={(item) => (
                          <div>
                            <div style={{
                              color: "rgba(255, 255, 255, 0.9)",
                              fontSize: "14px",
                              fontWeight: "500",
                              lineHeight: "1.5",
                              marginBottom: "8px",
                              wordBreak: "break-word"
                            }}>
                              {item.message.length > 200 ? `${item.message.slice(0, 200)}...` : item.message}
                            </div>
                            <div style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              fontSize: "12px",
                              color: "rgba(255, 255, 255, 0.5)"
                            }}>
                              <span>🕒 {new Date(item.created_at).toLocaleDateString()}</span>
                              {item.channel && (
                                <>
                                  <span>•</span>
                                  <span style={{
                                    background: "rgba(6, 182, 212, 0.2)",
                                    padding: "2px 8px",
                                    borderRadius: "8px",
                                    color: "#06b6d4",
                                    fontWeight: "600"
                                  }}>
                                    {item.channel}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                        emptyMessage="No chat messages recorded"
                      />
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                {/* Enhanced Search Section */}
                <div style={{ marginBottom: "32px" }}>
                  <div style={{ display: "flex", gap: "16px", marginBottom: "12px" }}>
                    <div style={{ position: "relative", flex: 1 }}>
                      <input
                        ref={inputRef}
                        className="search-input"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && runSearch()}
                        placeholder="Search by username or email..."
                        style={{
                          width: "100%",
                          padding: "16px 20px 16px 52px",
                          borderRadius: "16px",
                          border: "1px solid rgba(255, 255, 255, 0.12)",
                          background: "rgba(255, 255, 255, 0.04)",
                          color: "#ffffff",
                          fontSize: "16px",
                          outline: "none",
                          transition: "all 0.2s ease",
                          fontWeight: "500"
                        }}
                      />
                      <svg 
                        style={{
                          position: "absolute",
                          left: "18px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "rgba(255, 255, 255, 0.5)"
                        }}
                        width="20" 
                        height="20" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2"
                      >
                        <circle cx="11" cy="11" r="8" />
                        <path d="M21 21l-4.35-4.35" />
                      </svg>
                    </div>
                    
                    <button
                      type="button"
                      className="search-button"
                      onClick={runSearch}
                      disabled={loading || !query.trim()}
                      style={{
                        padding: "16px 32px",
                        borderRadius: "16px",
                        background: loading || !query.trim() 
                          ? "rgba(99, 102, 241, 0.3)" 
                          : "linear-gradient(135deg, rgba(99, 102, 241, 0.9), rgba(139, 92, 246, 0.9))",
                        color: "#ffffff",
                        border: "none",
                        fontWeight: "700",
                        fontSize: "16px",
                        cursor: loading || !query.trim() ? "not-allowed" : "pointer",
                        minWidth: "120px",
                        transition: "all 0.2s ease",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        boxShadow: loading || !query.trim() ? "none" : "0 4px 14px rgba(99, 102, 241, 0.3)"
                      }}
                    >
                      {loading ? (
                        <>
                          <div style={{
                            width: "16px",
                            height: "16px",
                            border: "2px solid rgba(255,255,255,0.3)",
                            borderTop: "2px solid #ffffff",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite"
                          }} />
                          Searching...
                        </>
                      ) : (
                        <>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" />
                            <path d="M21 21l-4.35-4.35" />
                          </svg>
                          Search
                        </>
                      )}
                    </button>
                  </div>
                  
                  {query.trim() && (
                    <div style={{ 
                      color: "rgba(255, 255, 255, 0.6)", 
                      fontSize: "14px",
                      marginTop: "12px",
                      fontWeight: "500"
                    }}>
                      Press Enter or click Search to find users matching "{query}"
                    </div>
                  )}
                </div>

                {/* Enhanced Error Display */}
                {error && (
                  <div style={{ 
                    color: "#f87171", 
                    background: "linear-gradient(135deg, rgba(248, 113, 113, 0.1), rgba(248, 113, 113, 0.05))",
                    border: "1px solid rgba(248, 113, 113, 0.2)",
                    borderRadius: "16px",
                    padding: "16px 20px",
                    fontSize: "15px", 
                    marginBottom: "24px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    fontWeight: "600"
                  }}>
                    <div style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "10px",
                      background: "rgba(248, 113, 113, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0
                    }}>
                      ⚠️
                    </div>
                    <div>
                      <div style={{ fontWeight: "700", marginBottom: "4px" }}>Search Error</div>
                      <div style={{ opacity: 0.8 }}>{error}</div>
                    </div>
                  </div>
                )}

                {/* Enhanced Results */}
                <div>
                  {results.length > 0 && (
                    <div style={{ 
                      color: "rgba(255, 255, 255, 0.8)", 
                      fontSize: "16px", 
                      marginBottom: "20px",
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px"
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20,6 9,17 4,12"/>
                      </svg>
                      Found {results.length} user{results.length !== 1 ? 's' : ''}
                    </div>
                  )}
                  
                  <div style={{ 
                    display: "flex", 
                    flexDirection: "column", 
                    gap: "12px",
                    maxHeight: "500px",
                    overflowY: "auto"
                  }}>
                    {results.map((user, index) => (
                      <div
                        key={user.id}
                        className="user-item"
                        onClick={() => handleUserSelect(user)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleUserSelect(user);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "20px",
                          padding: "20px 24px",
                          borderRadius: "16px",
                          background: "rgba(255, 255, 255, 0.04)",
                          border: "1px solid rgba(255, 255, 255, 0.08)",
                          cursor: "pointer",
                          animation: `slideUpStagger 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 50}ms both`
                        }}
                      >
                        <div style={{
                          width: "48px",
                          height: "48px",
                          borderRadius: "14px",
                          background: "linear-gradient(135deg, #60a5fa, #3b82f6)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          boxShadow: "0 4px 12px rgba(96, 165, 250, 0.3)"
                        }}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
                            <path d="M20 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M4 21v-2a4 4 0 0 1 3-3.87" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                        </div>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ 
                            color: "#ffffff", 
                            fontSize: "17px", 
                            fontWeight: "700",
                            marginBottom: "4px",
                            letterSpacing: "-0.01em"
                          }}>
                            {user.username}
                          </div>
                          <div style={{ 
                            color: "rgba(255, 255, 255, 0.7)", 
                            fontSize: "14px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontWeight: "500"
                          }}>
                            {user.email}
                          </div>
                        </div>

                        <div style={{
                          color: "#a5b4fc", 
                          fontSize: "12px",
                          fontFamily: "ui-monospace, monospace",
                          background: "rgba(165, 180, 252, 0.1)",
                          padding: "6px 12px",
                          borderRadius: "8px",
                          border: "1px solid rgba(165, 180, 252, 0.2)",
                          fontWeight: "600"
                        }}>
                          {user.id}
                        </div>

                        <svg 
                          width="20" 
                          height="20" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="rgba(255, 255, 255, 0.4)" 
                          strokeWidth="2"
                          style={{ flexShrink: 0 }}
                        >
                          <path d="M9 18l6-6-6-6" />
                        </svg>
                      </div>
                    ))}

                    {results.length === 0 && !loading && query.trim() && (
                      <div style={{ 
                        color: "rgba(255, 255, 255, 0.6)", 
                        fontSize: "16px", 
                        textAlign: "center",
                        padding: "60px 24px",
                        background: "rgba(255, 255, 255, 0.02)",
                        borderRadius: "20px",
                        border: "1px dashed rgba(255, 255, 255, 0.1)"
                      }}>
                        <div style={{ marginBottom: "16px", fontSize: "48px", opacity: 0.6 }}>🔍</div>
                        <div style={{ fontWeight: "600", marginBottom: "8px" }}>Ready to search</div>
                        <div style={{ color: "rgba(255, 255, 255, 0.5)" }}>
                          Click Search to query for "{query}"
                        </div>
                      </div>
                    )}
                    
                    {results.length === 0 && !loading && !query.trim() && (
                      <div style={{ 
                        color: "rgba(255, 255, 255, 0.5)", 
                        fontSize: "16px", 
                        textAlign: "center",
                        padding: "60px 24px"
                      }}>
                        <div style={{ marginBottom: "16px", fontSize: "48px", opacity: 0.4 }}>👥</div>
                        <div style={{ fontWeight: "600", marginBottom: "8px" }}>Start searching</div>
                        <div>Enter a username or email to find users</div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};